import React, { useState, useEffect } from 'react';
import { UserPreferences, ThemeId, AuthMethod } from '../types';
import './CustomizationPanel.css';

interface Theme {
  id: ThemeId;
  name: string;
  description: string;
  preview: {
    primary: string;
    secondary: string;
    accent: string;
  };
}

interface CustomizationPanelProps {
  preferences?: UserPreferences;
  profile?: any;
  onPreferencesChange: (preferences: UserPreferences) => void;
  onLogin?: (authMethod: AuthMethod, credentials?: any) => Promise<any>;
  onLogout?: () => void;
  isAuthenticated?: boolean;
  guestGamesRemaining?: number;
  onClose?: () => void;
}

const AVAILABLE_THEMES: Theme[] = [
  {
    id: 'default',
    name: 'Default',
    description: 'Clean and modern',
    preview: {
      primary: '#ffffff',
      secondary: '#f8f9fa',
      accent: '#3498db'
    }
  },
  {
    id: 'dark',
    name: 'Dark',
    description: 'Easy on the eyes',
    preview: {
      primary: '#2c3e50',
      secondary: '#34495e',
      accent: '#3498db'
    }
  },
  {
    id: 'neon',
    name: 'Neon',
    description: 'Cyberpunk vibes',
    preview: {
      primary: '#0a0a0a',
      secondary: '#1a1a1a',
      accent: '#00ff88'
    }
  },
  {
    id: 'classic',
    name: 'Classic',
    description: 'Timeless wood style',
    preview: {
      primary: '#f5deb3',
      secondary: '#deb887',
      accent: '#8b4513'
    }
  }
];

const CustomizationPanel: React.FC<CustomizationPanelProps> = ({
  preferences,
  profile,
  onPreferencesChange,
  onLogin,
  onLogout,
  isAuthenticated = false,
  guestGamesRemaining = 3,
  onClose
}) => {
  const defaultPreferences: UserPreferences = {
    theme: 'default',
    soundEnabled: true,
    defaultGameMode: 'single_player' as any,
    notifications: {
      turnBased: true,
      gameInvites: true,
      gameResults: true
    }
  };
  
  const [localPreferences, setLocalPreferences] = useState<UserPreferences>(
    preferences || profile?.preferences || defaultPreferences
  );
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const currentPrefs = preferences || profile?.preferences || defaultPreferences;
    const prefsChanged = JSON.stringify(localPreferences) !== JSON.stringify(currentPrefs);
    setHasChanges(prefsChanged);
  }, [localPreferences, preferences, profile?.preferences, defaultPreferences]);

  const handleThemeChange = (themeId: ThemeId) => {
    const newPreferences = {
      ...localPreferences,
      theme: themeId
    };
    setLocalPreferences(newPreferences);
  };

  const handleSoundToggle = () => {
    const newPreferences = {
      ...localPreferences,
      soundEnabled: !localPreferences.soundEnabled
    };
    setLocalPreferences(newPreferences);
  };

  const handleNotificationChange = (key: keyof typeof localPreferences.notifications, value: boolean) => {
    const newPreferences = {
      ...localPreferences,
      notifications: {
        ...localPreferences.notifications,
        [key]: value
      }
    };
    setLocalPreferences(newPreferences);
  };

  const handleSave = () => {
    onPreferencesChange(localPreferences);
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalPreferences(preferences || profile?.preferences || defaultPreferences);
    setHasChanges(false);
  };

  const handleClose = () => {
    if (hasChanges) {
      const shouldSave = window.confirm('You have unsaved changes. Do you want to save them?');
      if (shouldSave) {
        handleSave();
      }
    }
    onClose?.();
  };

  return (
    <div className="customization-panel">
      <div className="customization-header">
        <h2>Customization</h2>
        {onClose && (
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        )}
      </div>

      <div className="customization-content">
        {/* Authentication Section */}
        <div className="customization-section">
          <h3>Account</h3>
          {isAuthenticated ? (
            <div className="auth-section">
              <div className="profile-info">
                <span className="profile-name">{profile?.name || 'User'}</span>
                <span className="profile-email">{profile?.email}</span>
              </div>
              {onLogout && (
                <button className="auth-button auth-button--logout" onClick={onLogout}>
                  Sign Out
                </button>
              )}
            </div>
          ) : (
            <div className="auth-section">
              <div className="guest-status">
                <span className="guest-info">
                  Playing as guest ({guestGamesRemaining} games remaining)
                </span>
              </div>
              {onLogin && (
                <div className="auth-buttons">
                  <button 
                    className="auth-button auth-button--google"
                    onClick={() => onLogin && onLogin(AuthMethod.GOOGLE, {})}
                  >
                    Sign in with Google
                  </button>
                  <button 
                    className="auth-button auth-button--apple"
                    onClick={() => onLogin && onLogin(AuthMethod.APPLE, {})}
                  >
                    Sign in with Apple
                  </button>
                  <button 
                    className="auth-button auth-button--facebook"
                    onClick={() => onLogin && onLogin(AuthMethod.FACEBOOK, {})}
                  >
                    Sign in with Facebook
                  </button>
                  <button 
                    className="auth-button auth-button--yahoo"
                    onClick={() => onLogin && onLogin(AuthMethod.YAHOO, {})}
                  >
                    Sign in with Yahoo
                  </button>
                  <button 
                    className="auth-button auth-button--custom"
                    onClick={() => onLogin && onLogin(AuthMethod.CUSTOM, {})}
                  >
                    Create Account
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Theme Selection */}
        <div className="customization-section">
          <h3>Theme</h3>
          <div className="theme-grid">
            {AVAILABLE_THEMES.map((theme) => (
              <button
                key={theme.id}
                className={`theme-option ${
                  localPreferences.theme === theme.id ? 'theme-option--selected' : ''
                }`}
                onClick={() => handleThemeChange(theme.id)}
              >
                <div className="theme-preview">
                  <div 
                    className="theme-preview__primary"
                    style={{ backgroundColor: theme.preview.primary }}
                  />
                  <div 
                    className="theme-preview__secondary"
                    style={{ backgroundColor: theme.preview.secondary }}
                  />
                  <div 
                    className="theme-preview__accent"
                    style={{ backgroundColor: theme.preview.accent }}
                  />
                </div>
                <div className="theme-info">
                  <span className="theme-name">{theme.name}</span>
                  <span className="theme-description">{theme.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Sound Settings */}
        <div className="customization-section">
          <h3>Audio</h3>
          <div className="setting-item">
            <label className="setting-label">
              <span className="setting-text">
                <span className="setting-title">Sound Effects</span>
                <span className="setting-description">
                  Play sounds for moves and game events
                </span>
              </span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={localPreferences.soundEnabled}
                  onChange={handleSoundToggle}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="customization-section">
          <h3>Notifications</h3>
          
          <div className="setting-item">
            <label className="setting-label">
              <span className="setting-text">
                <span className="setting-title">Turn-based Game Alerts</span>
                <span className="setting-description">
                  Get notified when it's your turn
                </span>
              </span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={localPreferences.notifications.turnBased}
                  onChange={(e) => handleNotificationChange('turnBased', e.target.checked)}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <span className="setting-text">
                <span className="setting-title">Game Invites</span>
                <span className="setting-description">
                  Get notified about game invitations
                </span>
              </span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={localPreferences.notifications.gameInvites}
                  onChange={(e) => handleNotificationChange('gameInvites', e.target.checked)}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>

          <div className="setting-item">
            <label className="setting-label">
              <span className="setting-text">
                <span className="setting-title">Game Results</span>
                <span className="setting-description">
                  Get notified about game outcomes
                </span>
              </span>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={localPreferences.notifications.gameResults}
                  onChange={(e) => handleNotificationChange('gameResults', e.target.checked)}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
              </div>
            </label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      {hasChanges && (
        <div className="customization-actions">
          <button className="action-button action-button--secondary" onClick={handleReset}>
            Reset
          </button>
          <button className="action-button action-button--primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomizationPanel;